/**
 * Analytics View-All pages (Jun 2026 v2) — full-page drill-downs for
 * Processors / Clients / Top Performers / SLA categories / Backlog,
 * launched from the Historical Dashboard with all filters carried over.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Loader2, Search, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

const TITLES = {
  processors: 'All Processors — Productivity Comparison',
  clients: 'All Clients — Volumes',
  performers: 'All Performers — Balanced Score Ranking',
  sla: 'SLA Files',
  backlog: 'Backlog Details',
};
const BUCKET_LABEL = { within: 'Within SLA', near: 'Near SLA', over: 'Over SLA' };
const STATE_LABEL = { approaching: 'Approaching SLA', over: 'Over SLA' };
const SLA_BADGE = {
  within: 'bg-emerald-100 text-emerald-700', near: 'bg-amber-100 text-amber-700', over: 'bg-red-100 text-red-700',
  ok: 'bg-slate-100 text-slate-600', approaching: 'bg-amber-100 text-amber-700',
};

const COLUMNS = {
  processors: [
    ['name', 'Processor'], ['files', 'Files'], ['avg_ms', 'Avg Time', 'avg_label'], ['sla_pct', 'SLA %'],
    ['daily_avg', 'Daily Avg'], ['assigned', 'Assigned'], ['completion_rate', 'Compl. %'], ['change_pct', 'vs Prev %'],
  ],
  clients: [
    ['client', 'Client'], ['received', 'Received'], ['completed', 'Completed'], ['completion_pct', 'Completion %'],
    ['pending', 'Pending'], ['avg_ms', 'Avg Time', 'avg_label'], ['sla_pct', 'SLA %'], ['volume_change_pct', 'Volume vs Prev %'],
  ],
  performers: [
    ['rank', '#'], ['name', 'Processor'], ['score', 'Score'], ['files', 'Files'],
    ['avg_ms', 'Avg Time', 'avg_label'], ['sla_pct', 'SLA %'], ['change_pct', 'vs Prev %'], ['reason', 'Why ranked here'],
  ],
  sla: [
    ['order_number', 'Order #'], ['client', 'Client'], ['product', 'Product'], ['assignee', 'Processor'],
    ['completed_at', 'Completed'], ['dur_ms', 'Duration', 'dur_label'], ['sla_bucket', 'SLA'],
  ],
  backlog: [
    ['order_number', 'Order #'], ['client', 'Client'], ['product', 'Product'], ['assignee', 'Assignee'],
    ['status', 'Status'], ['created_at', 'Created'], ['age_days', 'Age', 'age_label'], ['sla_state', 'Risk'],
  ],
};

const FILTER_KEYS = ['start_date', 'end_date', 'order_type', 'product', 'client', 'user_id'];

export default function AnalyticsListPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const view = params.get('view') || 'processors';
  const bucket = params.get('bucket') || '';
  const state = params.get('state') || '';
  const minAge = params.get('min_age') || '';
  const serverSide = view === 'sla' || view === 'backlog';

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState(null);

  const filterQS = useMemo(() => {
    const qs = new URLSearchParams();
    FILTER_KEYS.forEach(kk => { const v = params.get(kk); if (v) qs.set(kk, v); });
    return qs.toString();
  }, [params]);

  useEffect(() => { const t = setTimeout(() => { setDebounced(search); setPage(0); }, 350); return () => clearTimeout(t); }, [search]);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      setLoading(true);
      try {
        if (serverSide) {
          const qs = new URLSearchParams(filterQS);
          qs.set('section', view);
          if (bucket) qs.set('bucket', bucket);
          if (state) qs.set('state', state);
          if (minAge) qs.set('min_age', minAge);
          if (debounced) qs.set('search', debounced);
          if (sort) { qs.set('sort_key', sort.key); qs.set('sort_dir', sort.dir); }
          qs.set('page', page + 1);
          qs.set('page_size', PAGE_SIZE);
          const res = await axios.get(`${API}/api/productivity-analytics/historical-dashboard/files?${qs}`, { signal: ctrl.signal });
          setRows(res.data.rows || []);
          setTotal(res.data.total || 0);
        } else {
          const res = await axios.get(`${API}/api/productivity-analytics/historical-dashboard?${filterQS}`, { signal: ctrl.signal });
          const d = res.data;
          const src = view === 'clients' ? d.client_metrics
            : view === 'performers' ? [...(d.processor_metrics || [])] : d.processor_metrics;
          if (view === 'performers') {
            // recompute full performer list ordering from top_performers scores where present
            const scored = new Map((d.top_performers || []).map(p => [p.user_id, p]));
            const merged = (d.processor_metrics || []).filter(p => p.user_id !== 'unassigned').map(p => ({
              ...p, ...(scored.get(p.user_id) || {}),
            })).sort((a, b) => (b.score ?? -1) - (a.score ?? -1) || b.files - a.files)
              .map((p, i) => ({ ...p, rank: i + 1 }));
            setRows(merged);
            setTotal(merged.length);
          } else {
            setRows(src || []);
            setTotal((src || []).length);
          }
        }
      } catch (e) {
        if (!axios.isCancel(e) && e.code !== 'ERR_CANCELED') {
          console.error('view-all fetch failed:', e);
          toast.error('Failed to load data');
        }
      } finally { setLoading(false); }
    })();
    return () => ctrl.abort();
  }, [view, bucket, state, minAge, filterQS, debounced, page, sort, serverSide]);

  const visibleRows = useMemo(() => {
    if (serverSide) return rows;
    let r = rows;
    if (debounced) {
      const q = debounced.toLowerCase();
      r = r.filter(row => ['name', 'client', 'product'].some(f => String(row[f] || '').toLowerCase().includes(q)));
    }
    if (sort) {
      r = [...r].sort((a, b) => {
        const av = a[sort.key] ?? -Infinity, bv = b[sort.key] ?? -Infinity;
        return sort.dir * (av > bv ? 1 : av < bv ? -1 : 0);
      });
    }
    return r;
  }, [rows, debounced, sort, serverSide]);
  const pagedRows = serverSide ? visibleRows : visibleRows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const totalRows = serverSide ? total : visibleRows.length;
  const pages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));

  const toggleSort = (key) => { setSort(s => (s?.key === key ? { key, dir: -s.dir } : { key, dir: -1 })); setPage(0); };

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/api/productivity-analytics/historical-dashboard/export?${filterQS}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `historical_analytics_${params.get('start_date')}_${params.get('end_date')}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
    setExporting(false);
  };

  const cols = COLUMNS[view] || COLUMNS.processors;
  const title = view === 'sla' ? `${BUCKET_LABEL[bucket] || 'All'} — Completed Files`
    : view === 'backlog' && minAge ? `Backlog — Pending >${minAge} Days`
    : view === 'backlog' && state ? `Backlog — ${STATE_LABEL[state] || state}`
    : TITLES[view] || TITLES.processors;
  const filterChips = [
    ...FILTER_KEYS.map(kk => [kk, params.get(kk)]),
    ['sla', BUCKET_LABEL[bucket]],
    ['risk', STATE_LABEL[state]],
    ['min age', minAge ? `>${minAge}d` : null],
  ].filter(([, v]) => v);

  const cellValue = (row, [key, , labelKey]) => {
    const v = labelKey ? row[labelKey] : row[key];
    if (key === 'sla_bucket' || key === 'sla_state') {
      return <Badge className={`${SLA_BADGE[row[key]] || 'bg-slate-100 text-slate-600'} border-0 font-medium`}>{BUCKET_LABEL[row[key]] || STATE_LABEL[row[key]] || (row[key] === 'ok' ? 'On track' : row[key])}</Badge>;
    }
    if (key === 'sla_pct' && v != null) {
      return <span className={`font-medium ${v >= 95 ? 'text-emerald-600' : v >= 85 ? 'text-amber-600' : 'text-red-500'}`}>{v}%</span>;
    }
    if ((key === 'completion_pct' || key === 'completion_rate') && v != null) return `${v}%`;
    if ((key === 'change_pct' || key === 'volume_change_pct') && v != null) {
      return <span className={v >= 0 ? 'text-emerald-600 font-medium' : 'text-red-500 font-medium'}>{v > 0 ? '+' : ''}{v}%</span>;
    }
    if (key === 'score' && v != null) return <span className="text-xs font-bold text-white bg-slate-900 rounded-md px-1.5 py-0.5">{v}</span>;
    if (key === 'reason') return <span className="text-xs text-slate-500">{v}</span>;
    return v ?? '—';
  };

  return (
    <div className="space-y-4" data-testid="analytics-list-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate(`/analytics/productivity?${filterQS}`)} data-testid="list-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Analytics
        </Button>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <div className="flex items-center gap-1.5 flex-wrap mt-0.5" data-testid="list-filter-chips">
            {filterChips.map(([kk, v]) => (
              <span key={kk} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                {kk.replace('_', ' ')}: {v}
              </span>
            ))}
          </div>
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
            className="h-8 w-48 rounded-md border border-slate-200 pl-8 pr-2 text-sm" data-testid="list-search" />
        </div>
        <Button variant="outline" size="sm" onClick={doExport} disabled={exporting} data-testid="list-export">
          {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}Export
        </Button>
      </div>

      <Card className="border-slate-200 shadow-sm rounded-xl">
        <CardContent className="p-5">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="tf-thead text-left text-[11px] uppercase tracking-wide">
                      {cols.map(([key, label]) => (
                        <th key={key} className="py-2 pr-3 cursor-pointer select-none whitespace-nowrap"
                          onClick={() => toggleSort(key)} data-testid={`list-sort-${key}`}>
                          {label}{sort?.key === key ? (sort.dir === -1 ? ' ↓' : ' ↑') : ''}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row, i) => (
                      <tr key={`${row.order_number || row.user_id || row.client || ''}-${i}`} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`list-row-${i}`}>
                        {cols.map((col) => <td key={col[0]} className="py-2 pr-3">{cellValue(row, col)}</td>)}
                      </tr>
                    ))}
                    {!pagedRows.length && <tr><td colSpan={cols.length} className="py-10 text-center text-slate-400">No data for this period</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                <span data-testid="list-total">{totalRows} record{totalRows !== 1 ? 's' : ''}</span>
                {pages > 1 && (
                  <div className="flex items-center gap-2">
                    <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40" data-testid="list-prev">Prev</button>
                    <span data-testid="list-page">{page + 1} / {pages}</span>
                    <button disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)} className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40" data-testid="list-next">Next</button>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
